import React, { useState, useMemo } from 'react';
import { useAppSelector } from '../../../hooks/storeHooks';
import { RootState } from '../../../store';
import ExportTable from '../../../components/common/export-table/ExportTable';
import Loader from '../../common/Loader/Loader';
import RecommendationsTable from './RecommendationsTable';
import './RecommendationTableWidget.scss';
import { getDecimals, isNumericField } from './TableData/RecommendationsTableData';
import Helpers from '../../../utilities/Helpers';
import { formatDate } from '../../../utilities/dateHelpers';

interface RecommendationsTableWidgetProps {
  widgetHeader: string;
  columnData: any[];
  tableData: any[];
  statusLegend?: boolean;
}

const RecommendationsTableWidget: React.FC<RecommendationsTableWidgetProps> = ({
  widgetHeader,
  columnData,
  tableData,
  statusLegend,
}) => {
  const loading = useAppSelector((state: RootState) => state.Recommendations.frequencyChange.loading);

  const [searchTerm, setSearchTerm] = useState<string>('');
  const rPermission = { canExport: true };

  // Filter data based on search term
  const filteredTableData = useMemo(() => {
    if (!Array.isArray(tableData)) {
      return [];
    }
    return tableData || [];
  }, [tableData]);

  // Export functionality
  const tableRowsForExport = useMemo(() => {
    return filteredTableData.map((item) => ({ original: item }));
  }, [filteredTableData]);

  // Handle search input change
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  // Generate export columns dynamically based on table columns
  const exportColumns = useMemo(() => {
    const flattenedColumns: any[] = [];

    const processColumn = (column: any, groupHeader?: string) => {
      const header = typeof column.Header === 'string' ? Helpers.toSentenceCase(column.Header) : '';
      const accessor = column.accessor as string;
      const columnId = column.id || accessor;

      return {
        key: columnId,
        header: header,
        groupHeader: groupHeader ? Helpers.toSentenceCase(groupHeader) : '',
        accessor: (row: any) => {
          // Handle special cases for nested data
          if (columnId.toLowerCase().includes('manufacturer')) {
            const eq = row.original?.Equipment;
            return eq?.Pump1?.Manufacturer || eq?.Pump2?.Manufacturer || eq?.Pump3?.Manufacturer || '';
          }
          if (columnId === 'equipment' || columnId === 'DisplayEquipment' || columnId === 'Equipment') {
            return row.original?.Equipment?.DisplayEquipment || row.original?.EspEquipment?.DisplayEquipment || '';
          }
          if (columnId === 'Status') {
            // Convert status to legend text for export consistency
            const statusValue = row.original?.[accessor] || '';
            const lowerStatus = statusValue?.toLowerCase() || '';
            if (lowerStatus.includes('pending')) return 'Awaiting test';
            if (lowerStatus.includes('tracked') && !lowerStatus.includes('no longer')) return 'Actively tracked';
            if (lowerStatus.includes('no longer tracked')) return 'No longer tracked';
            return statusValue;
          }

          // Handle date columns
          if (
            [
              'InstallDate',
              'StartDate',
              'FailDate',
              'PullDate',
              'DateString',
              'BeforeDateString',
              'AfterDateString',
            ].includes(columnId)
          ) {
            const dateValue = row.original?.[accessor];
            if (!dateValue) return '';
            const date = new Date(dateValue);
            if (isNaN(date.getTime())) return dateValue; // Return as-is if invalid

            return formatDate(dateValue);
          }

          // Handle WellName column
          if (columnId === 'WellName') {
            return row.original?.[accessor] || '';
          }

          if (column.Header === 'Task' && (!columnId || columnId === '')) {
            return 'Recommend';
          }

          if (
            ['NotFeasibleFrequencyChangeString', 'NotFeasibleInjectionIncrease', 'NotFeasibleFrequencyChange'].includes(
              columnId,
            )
          ) {
            const value = row.original?.[accessor];
            return value === 'Yes' || !!value ? 'Yes' : 'No';
          }

          // Handle numeric fields with proper formatting
          if (isNumericField(columnId)) {
            const value = row.original?.[accessor];
            if (value === null || value === undefined || isNaN(value)) return '';

            // Get decimal places for specific fields
            const decimals = getDecimals(columnId);
            return Number(value).toFixed(decimals);
          }

          if (column.Cell && typeof column.Cell === 'function') {
            try {
              const cellResult = column.Cell({ value: row.original?.[accessor], row });
              if (cellResult && typeof cellResult === 'object' && cellResult.props) {
                if (cellResult.props.children) {
                  return String(cellResult.props.children);
                }
              }
              return String(cellResult || '');
            } catch (e) {
              return row.original?.[accessor] || '';
            }
          }
          // For regular accessors
          if (accessor) {
            const value = row.original?.[accessor];
            if (value === null || value === undefined) return '';
            if (typeof value === 'object') return JSON.stringify(value);
            return String(value);
          }
          return '';
        },
      };
    };

    columnData
      .filter((column) => column.id !== 'actions' && column.id !== 'delete-actions') // Exclude action columns from export
      .forEach((column) => {
        // Check if this is a grouped column
        if (column.columns && Array.isArray(column.columns)) {
          const groupHeader = typeof column.Header === 'string' ? column.Header : '';
          column.columns.forEach((subColumn: any) => {
            flattenedColumns.push(processColumn(subColumn, groupHeader));
          });
        } else {
          flattenedColumns.push(processColumn(column));
        }
      });

    return flattenedColumns;
  }, [columnData]);

  return (
    <div className='recommendations-main-container table-wrap'>
      <header className='cust-table-header'>
        <div className='cust-table-header-wrap'>
          <h1 className='table-header-title'>
            {widgetHeader} ({filteredTableData.length})
          </h1>
        </div>
      </header>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          margin: '16px 20px',
        }}
      >
        <div className='recommended-export-section'>
          <ExportTable
            data={tableRowsForExport}
            fileName={`${widgetHeader}`}
            sheetName='Frequency Change'
            columns={exportColumns}
            disabled={loading || !tableRowsForExport || tableRowsForExport.length === 0}
            rPermission={rPermission}
          />
          {statusLegend && (
            <div className='recommended-status-legend'>
              <div className='recommended-legend-item'>
                <div className='recommended-legend-bullet awaiting-test' />
                <span className='recommended-legend-text'>Awaiting test</span>
              </div>
              <div className='recommended-legend-item'>
                <div className='recommended-legend-bullet actively-tracked' />
                <span className='recommended-legend-text'>Actively tracked</span>
              </div>
              <div className='recommended-legend-item'>
                <div className='recommended-legend-bullet no-longer-tracked' />
                <span className='recommended-legend-text'>No longer tracked</span>
              </div>
            </div>
          )}
        </div>
        <div className='flex flex-col gap-[6px] items-start self-stretch shrink-0 flex-nowrap relative w-[20%]'>
          <div className='flex pt-[7px] pr-[12px] pb-[6px] pl-[12px] gap-[8px] items-center self-stretch shrink-0 flex-nowrap bg-[#001023] rounded-[8px] border-solid border border-[#4a5463] relative shadow-[0_1px_2px_0_rgba(16,24,40,0.05)] search-input w-full'>
            <span className='ico-search' />
            <div className='flex w-full gap-[8px] items-center shrink-0 flex-nowrap relative custom-view-search'>
              <input
                type='text'
                placeholder='Search'
                value={searchTerm}
                onChange={handleSearchChange}
                style={{ border: 'none', background: 'transparent' }}
                className="flex w-full h-[24px] justify-start items-start shrink-0 basis-auto font-['Mulish'] text-[14px] font-normal leading-[24px] text-[#b8c5cc] relative text-left whitespace-nowrap bg-transparent border-none"
              />
            </div>
            {searchTerm && (
              <button
                className='clear-search-button'
                onClick={() => setSearchTerm('')}
                type='button'
                aria-label='Clear search'
              >
                ✕
              </button>
            )}
          </div>
        </div>
      </div>
      {loading ? (
        <div className='alarm-loading-state'>
          <Loader />
        </div>
      ) : (
        <div className='alarm-table-wrapper'>
          <RecommendationsTable
            data={filteredTableData}
            columns={columnData}
            searchTerm={searchTerm}
            widgetHeader={widgetHeader}
          />
        </div>
      )}
    </div>
  );
};

export default RecommendationsTableWidget;
